import React, { FC, useState, useMemo, useEffect } from 'react'
import InfoIcon from 'transfix-icons/build/InfoIcon'
import NotAvailableIcon from 'transfix-icons/build/NotAvailableIcon'
import {
  IFilterAutocompleteProp,
  isAutosuggestResult,
} from 'transfix-ui/components/FilterAutocomplete'
import { Spinner } from 'transfix-ui/components/Spinner'
import { Tag, TAG_SIZE, TAG_STYLE } from 'transfix-ui/components/Tag'
import Box from 'transfix-ui/core/Box'
import Typography from 'transfix-ui/core/Typography'
import {
  ShipperStrikePrices,
  useGetShipperStrikePrices,
} from 'apps/marketplace/apiTypes/shipperStrikePrices'
import ShipperFilterAutocomplete from 'apps/marketplace/components/ShipperFilterAutocomplete'
import { ORDERED_REGIONS } from 'apps/spot-quote-control-panel/constants/shipperStrikePrices'
import { IShipper } from 'apps/spot-quote-control-panel/types/shipperStrikePrices'
import { useModal } from 'utils/hooks'
import { StrikePriceDeleteOverrideModal } from './StrikePriceDeleteOverrideModal'
import { StrikePriceOverrideModal } from './StrikePriceOverrideModal'
import { StrikePriceTable } from './StrikePriceTable'

interface StrikePricesCollection {
  [key: string]: ShipperStrikePrices[]
}

interface IStrikePriceShipperResultsProps {
  currentShipper?: IShipper
  onSetCurrentShipper: (shipper: IShipper) => void
  doNotBidRegions: string[]
}

export const StrikePriceShipperResults: FC<IStrikePriceShipperResultsProps> = ({
  currentShipper,
  doNotBidRegions,
  onSetCurrentShipper,
}) => {
  const [currentStrikePrice, setCurrentStrikePrice] = useState<ShipperStrikePrices | undefined>(
    undefined
  )
  const { closeModal, isOpen, openModal } = useModal()
  const [overrideModal, deleteOverrideModal] = ['overrideModal', 'deleteOverrideModal']
  const openOverrideModal = openModal(overrideModal)
  const closeOverrideModal = () => {
    setCurrentStrikePrice(undefined)
    closeModal()
  }
  const addOrEditOverride = (strikePrice: ShipperStrikePrices) => {
    setCurrentStrikePrice(strikePrice)
    openOverrideModal()
  }
  const openDeleteOverrideModal = openModal(deleteOverrideModal)
  const deleteOverride = (strikePrice: ShipperStrikePrices) => {
    setCurrentStrikePrice(strikePrice)
    openDeleteOverrideModal()
  }
  const {
    data: strikePrices,
    loading: strikePricesLoading,
    refetch: fetchStrikePrices,
  } = useGetShipperStrikePrices({
    queryParams: {
      filters: {
        shipperApplicationId: currentShipper?.id || 0,
      },
    },
    lazy: true,
  })
  const shipperStrikePrices: ShipperStrikePrices[] = strikePrices?.shipperStrikePrices || []
  const sortedPricesByRegion: StrikePricesCollection = useMemo(
    () =>
      shipperStrikePrices.reduce((all, current) => {
        if (current.region) {
          all[current.region] = all[current.region] || []
          all[current.region].push(current)
        }
        return all
      }, {} as StrikePricesCollection),
    [shipperStrikePrices]
  )
  const onShipperChange = (val: IFilterAutocompleteProp) => {
    if (isAutosuggestResult(val)) {
      onSetCurrentShipper({
        id: Number(val.id),
        name: val.name,
      })
    }
  }
  const onOverrideRequestSuccess = () => {
    closeModal()
    fetchStrikePrices()
  }

  useEffect(() => {
    if (currentShipper?.id) {
      fetchStrikePrices()
    }
  }, [currentShipper, doNotBidRegions])

  return (
    <Box data-test='strike-price-shipper-results'>
      <Box width='auto' mb={2}>
        <ShipperFilterAutocomplete label='Select Shipper' onSelectedValue={onShipperChange} />
      </Box>
      {strikePricesLoading && <Spinner />}
      {currentShipper?.name && (
        <Box mb={2}>
          <Typography variant='h2'>{currentShipper.name}</Typography>
        </Box>
      )}
      {!strikePricesLoading && shipperStrikePrices.length === 0 && currentShipper?.id && (
        <Typography variant='h3'>No matching strike price data found.</Typography>
      )}
      {!strikePricesLoading && !currentShipper?.id && (
        <Typography variant='h3'>Select a shipper to view strike price data.</Typography>
      )}
      {!strikePricesLoading &&
        ORDERED_REGIONS.map(region =>
          sortedPricesByRegion[region] ? (
            <Box key={region} mb={4}>
              <Box display='flex' alignItems='center' justifyContent='start'>
                <Typography variant='h3'>
                  <span style={{ fontWeight: 700 }}>{region}</span> TCM50
                </Typography>
                {sortedPricesByRegion[region].every(price => price.isDefault) && (
                  <Box ml={2}>
                    <Tag
                      size={TAG_SIZE.small}
                      tagStyle={TAG_STYLE.info}
                      label={
                        <Box display='flex' alignItems='center'>
                          <InfoIcon /> <Box ml={1}>Default settings</Box>
                        </Box>
                      }
                    />
                  </Box>
                )}
                {doNotBidRegions.includes(region) && (
                  <Box ml={2}>
                    <Tag
                      size={TAG_SIZE.small}
                      tagStyle={TAG_STYLE.error}
                      label={
                        <Box display='flex' alignItems='center'>
                          <NotAvailableIcon /> <Box ml={1}>Do not bid</Box>
                        </Box>
                      }
                    />
                  </Box>
                )}
              </Box>
              <StrikePriceTable
                addOrEditOverride={addOrEditOverride}
                deleteOverride={deleteOverride}
                shipperStrikePrices={sortedPricesByRegion[region]}
              />
            </Box>
          ) : null
        )}
      {isOpen(overrideModal) && (
        <StrikePriceOverrideModal
          strikePrice={currentStrikePrice}
          onClose={closeOverrideModal}
          onRequestSuccess={onOverrideRequestSuccess}
        />
      )}
      {isOpen(deleteOverrideModal) && (
        <StrikePriceDeleteOverrideModal
          overrideId={currentStrikePrice?.manualOverride?.id}
          onClose={closeModal}
          onRequestSuccess={onOverrideRequestSuccess}
        />
      )}
    </Box>
  )
}
